import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { addPlssLayer } from '../lib/plss.js';

// MapLibre map with:
//  - parcels source/layer (Phase 1, blue)
//  - reverse source/layer (Phase 5, green) for reverse-lookup highlights
//  - draw source/layer (polygon drawing mode)
//  - PLSS raster overlay (Phase 2)
export default function Map({
  parcels,
  reverseGeom,
  drawMode,
  finishSignal,
  onPolygonDrawn,
  onVertexCountChange,
  flyTo,
  baseLayer = 'street',
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap contributors',
          },
          satellite: {
            type: 'raster',
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
            attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
          },
        },
        layers: [
          { id: 'osm', type: 'raster', source: 'osm', layout: { visibility: 'visible' } },
          { id: 'satellite', type: 'raster', source: 'satellite', layout: { visibility: 'none' } },
        ],
      },
      center: [-98.5795, 39.8283],
      zoom: 4,
      // Required so map.getCanvas().toDataURL() returns a real image
      // instead of a blank PNG. Small memory cost, needed for PDF export.
      preserveDrawingBuffer: true,
    });

    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'imperial' }), 'bottom-left');

    map.on('load', () => {
      // Parcels (forward lookup, blue)
      map.addSource('parcels', { type: 'geojson', data: empty() });
      map.addLayer({
        id: 'parcels-fill', type: 'fill', source: 'parcels',
        paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.15 },
      });
      map.addLayer({
        id: 'parcels-outline', type: 'line', source: 'parcels',
        paint: { 'line-color': '#1d4ed8', 'line-width': 2 },
      });

      // Reverse-lookup geometry
      // - aliquot_container: faded green (the PLSS reference area)
      // - metes_walk: darker orange (the specific tract inside it)
      map.addSource('reverse', { type: 'geojson', data: empty() });
      map.addLayer({
        id: 'reverse-container-fill', type: 'fill', source: 'reverse',
        filter: ['!=', ['get', 'kind'], 'metes_walk'],
        paint: { 'fill-color': '#34d399', 'fill-opacity': 0.18 },
      });
      map.addLayer({
        id: 'reverse-container-outline', type: 'line', source: 'reverse',
        filter: ['!=', ['get', 'kind'], 'metes_walk'],
        paint: { 'line-color': '#065f46', 'line-width': 2, 'line-dasharray': [2, 2] },
      });
      map.addLayer({
        id: 'reverse-metes-fill', type: 'fill', source: 'reverse',
        filter: ['==', ['get', 'kind'], 'metes_walk'],
        paint: { 'fill-color': '#ea580c', 'fill-opacity': 0.35 },
      });
      map.addLayer({
        id: 'reverse-metes-outline', type: 'line', source: 'reverse',
        filter: ['==', ['get', 'kind'], 'metes_walk'],
        paint: { 'line-color': '#9a3412', 'line-width': 3 },
      });

      // Draw (in-progress polygon)
      map.addSource('draw', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'draw-fill', type: 'fill', source: 'draw',
        paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.2 } });
      map.addLayer({ id: 'draw-outline', type: 'line', source: 'draw',
        paint: { 'line-color': '#d97706', 'line-width': 2, 'line-dasharray': [2, 2] } });
      map.addLayer({
        id: 'draw-points', type: 'circle', source: 'draw',
        filter: ['==', '$type', 'Point'],
        paint: { 'circle-radius': 5, 'circle-color': '#d97706',
          'circle-stroke-color': 'white', 'circle-stroke-width': 2 },
      });

      // Phase 2: PLSS overlay
      addPlssLayer(map);

      setMapReady(true);
    });

    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Update parcels source + fit bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('parcels');
    if (src) src.setData(parcels || empty());
    if (parcels?.features?.length) {
      const b = new maplibregl.LngLatBounds();
      for (const f of parcels.features) extendBounds(b, f.geometry);
      if (!b.isEmpty()) map.fitBounds(b, { padding: 60, maxZoom: 18, duration: 800 });
    }
  }, [parcels, mapReady]);

  // Update reverse-lookup source + fit bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('reverse');
    if (src) src.setData(reverseGeom || empty());
    if (reverseGeom?.features?.length) {
      const b = new maplibregl.LngLatBounds();
      for (const f of reverseGeom.features) extendBounds(b, f.geometry);
      if (!b.isEmpty()) map.fitBounds(b, { padding: 80, maxZoom: 16, duration: 800 });
    }
  }, [reverseGeom, mapReady]);

  // flyTo
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !flyTo) return;
    map.flyTo({ center: [flyTo.lng, flyTo.lat], zoom: flyTo.zoom || 17, duration: 800 });
  }, [flyTo, mapReady]);

  // Base layer toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (baseLayer === 'satellite') {
      map.setLayoutProperty('osm', 'visibility', 'none');
      map.setLayoutProperty('satellite', 'visibility', 'visible');
    } else {
      map.setLayoutProperty('osm', 'visibility', 'visible');
      map.setLayoutProperty('satellite', 'visibility', 'none');
    }
  }, [baseLayer, mapReady]);

  // Draw mode
  const verticesRef = useRef([]);
  const renderDraw = (map) => {
    const src = map.getSource('draw');
    if (!src) return;
    const v = verticesRef.current;
    const feats = [];
    for (const [lng, lat] of v) {
      feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: {} });
    }
    if (v.length >= 2) {
      const ring = v.length >= 3 ? [...v, v[0]] : v;
      feats.push({
        type: 'Feature',
        geometry: { type: v.length >= 3 ? 'Polygon' : 'LineString', coordinates: v.length >= 3 ? [ring] : ring },
        properties: {},
      });
    }
    src.setData({ type: 'FeatureCollection', features: feats });
  };
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const handleClick = (e) => {
      verticesRef.current.push([e.lngLat.lng, e.lngLat.lat]);
      onVertexCountChange?.(verticesRef.current.length);
      renderDraw(map);
    };
    if (drawMode) {
      verticesRef.current = [];
      onVertexCountChange?.(0);
      map.getCanvas().style.cursor = 'crosshair';
      map.on('click', handleClick);
    }
    return () => {
      map.getCanvas().style.cursor = '';
      map.off('click', handleClick);
      verticesRef.current = [];
      onVertexCountChange?.(0);
      const src = map.getSource('draw');
      if (src) src.setData(empty());
    };
  }, [drawMode, mapReady, onVertexCountChange]);

  const firstFinishRef = useRef(true);
  useEffect(() => {
    if (firstFinishRef.current) { firstFinishRef.current = false; return; }
    const map = mapRef.current;
    if (!map || !mapReady || !drawMode) return;
    const v = verticesRef.current;
    if (v.length < 3) return;
    const ring = [...v, v[0]];
    const polygon = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} };
    verticesRef.current = [];
    onVertexCountChange?.(0);
    renderDraw(map);
    onPolygonDrawn?.(polygon);
  }, [finishSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div id="map" ref={containerRef} />
      <MapLegend />
    </div>
  );
}

function MapLegend() {
  const [open, setOpen] = useState(true);
  const row = {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
    marginTop: 4, color: '#111827', lineHeight: 1.2,
  };
  const swatch = {
    width: 22, height: 12, borderRadius: 2, flexShrink: 0,
  };
  return (
    <div
      style={{
        position: 'absolute', top: 12, left: 12, zIndex: 1,
        background: 'rgba(255,255,255,0.92)',
        border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontFamily: 'inherit',
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', color: '#4b5563',
          textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none',
        }}
      >
        Legend {open ? '−' : '+'}
      </div>
      {open && (
        <div style={{ marginTop: 4 }}>
          <div style={row}>
            <span style={{ ...swatch, background: 'rgba(37,99,235,0.15)', border: '2px solid #1d4ed8' }} />
            <span>Regrid parcel</span>
          </div>
          <div style={row}>
            <span style={{ ...swatch, background: 'rgba(52,211,153,0.18)', border: '2px dashed #065f46' }} />
            <span>PLSS aliquot container</span>
          </div>
          <div style={row}>
            <span style={{ ...swatch, background: 'rgba(234,88,12,0.35)', border: '2px solid #9a3412' }} />
            <span>Metes-and-bounds walk</span>
          </div>
          <div style={row}>
            <span style={{ ...swatch, background: 'rgba(246,135,179,0.3)', border: '1px dashed #c93b6a' }} />
            <span>BLM PLSS grid (zoom 8+)</span>
          </div>
          <div style={row}>
            <span style={{ ...swatch, background: 'rgba(245,158,11,0.2)', border: '2px dashed #d97706' }} />
            <span>Drawn polygon (in progress)</span>
          </div>
        </div>
      )}
    </div>
  );
}

function empty() { return { type: 'FeatureCollection', features: [] }; }

function extendBounds(b, geom) {
  if (!geom) return;
  if (geom.type === 'Polygon') {
    for (const ring of geom.coordinates) for (const c of ring) b.extend(c);
  } else if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) for (const ring of poly) for (const c of ring) b.extend(c);
  }
}
